import { useState } from 'react';
import { Mail, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email) return toast.error('Enter your email');
    setBusy(true);
    try {
      const r = await api.post('/auth/forgot-password-v2', { email, resetUrl: window.location.origin + '/reset-password' });
      setSent(true);
      if (r.data?.error) toast.error(r.data.error);
      else toast.success(r.data?.sent ? 'Reset link sent — check your email' : 'If that email exists, a link was sent');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to send reset email');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="bg-primary/10 text-primary p-2 rounded-lg"><KeyRound size={18} /></div>
          <h1 className="text-xl font-bold text-slate-900">Forgot your password?</h1>
        </div>
        {sent ? (
          <>
            <p className="text-sm text-slate-600 mb-4">
              If <b>{email}</b> exists in our system you'll receive an email with a reset link. It expires in 15 minutes.
            </p>
            <a href="/login" className="text-primary text-sm hover:underline">← Back to sign in</a>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-4">
              Enter the email you used to sign in. We'll send a link to set a new password. Works for internal staff, merchants and their sub-users. (Super Admin accounts are reset from Settings while signed in.)
            </p>
            <label className="text-xs font-medium text-slate-700">Email</label>
            <div className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg mt-1 focus-within:border-primary">
              <Mail size={16} className="text-slate-400" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()}
                placeholder="you@company.com" className="flex-1 outline-none text-sm" autoFocus />
            </div>
            <button onClick={submit} disabled={busy || !email}
              className="w-full mt-4 bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              Send reset link
            </button>
            <div className="text-center mt-3">
              <a href="/login" className="text-primary text-sm hover:underline">← Back to sign in</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
